import type { Vehicle } from '../types/Vehicle';
import { api } from './api';
import type { PaginatedListResponse } from './response_models/PaginatedListIResponse';
import type { GetVehicleStatsResponse } from './response_models/vehicles/GetVehiclesStatsResponse';
interface GetVehiclesRequest {
  page?: number;
  pageSize?: number;
  searchText: string;
  speed_threshold_range_min?: number;
  speed_threshold_range_max?: number;
  ordering?: string;
}

interface VehicleListRequestParams {
  page?: number;
  pageSize?: number;
  searchText?: string;
}

interface VehicleRequestObject {
  plate_no: string;
  device: string;
  speed_threshold: number;
  organization: string;
}

export const vehiclesApi = api.enhanceEndpoints({ addTagTypes: ['vehicles'] }).injectEndpoints({
  endpoints: builder => ({
    getVehicleStatus: builder.query<Vehicle[], void>({
      query: () => {
        return `vehicles/status/`;
      },
    }),
    getAllVehicles: builder.query<PaginatedListResponse<Vehicle>, void>({
      query: () => {
        return `vehicles/`;
      },
    }),
    getVehicles: builder.query<PaginatedListResponse<Vehicle>, GetVehiclesRequest | void>({
      query: arg => {
        let queryString = `vehicles/`;

        if (arg?.page) {
          queryString += `?page=${arg.page}`;
        }

        if (arg?.pageSize) {
          queryString += `&page_size=${arg.pageSize}`;
        }

        if (arg?.searchText) {
          queryString += `&search=${arg.searchText}`;
        }
        if (arg?.speed_threshold_range_min !== undefined) {
          queryString += `&min_speed_threshold=${arg.speed_threshold_range_min}`;
        }
        if (arg?.speed_threshold_range_max !== undefined) {
          queryString += `&max_speed_threshold=${arg.speed_threshold_range_max}`;
        }

        if (arg?.ordering && arg?.ordering !== null) {
          queryString += `&ordering=${arg.ordering}`;
        }

        return queryString;
      },
      providesTags: (result, _, arg) => {
        try {
          if (result) {
            return [{ type: 'vehicles', id: `page=${arg?.page ?? 1}_size=${arg?.pageSize ?? 10}` }];
          }
        } catch (e) {
          console.error(e);
          return [];
        }
        return [{ type: 'vehicles', id: 'LIST' }];
      },
    }),
    getVehicleStats: builder.query<GetVehicleStatsResponse, void>({
      query: () => `vehicles/stats/`,
    }),

    listAllVehicle: builder.query<Partial<Vehicle[]>, void>({
      query: () => 'list_vehicles/',
    }),
    getVehicleList: builder.query<PaginatedListResponse<Vehicle>, VehicleListRequestParams>({
      query: ({ page, pageSize, searchText }) => ({
        url: `list_vehicles_detail/`,
        method: 'GET',
        params: { page, page_size: pageSize, search: searchText },
      }),
    }),
    createVehicle: builder.mutation<Vehicle, VehicleRequestObject>({
      query: vehicle => ({
        url: `vehicles/`,
        method: 'POST',
        body: vehicle,
      }),
    }),
    updateVehicle: builder.mutation<Vehicle, { vehicle_id: string; vehicle: VehicleRequestObject }>({
      query: ({ vehicle_id, vehicle }) => ({
        url: `update_vehicle/${vehicle_id}/`,
        method: 'PUT',
        body: vehicle,
      }),
    }),
    deleteVehicle: builder.mutation<Vehicle, { vehicle_id: string }>({
      query: ({ vehicle_id }) => ({
        url: `update_vehicle/${vehicle_id}/`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetAllVehiclesQuery,
  useLazyGetVehiclesQuery,
  useLazyGetVehicleStatusQuery,
  useLazyGetVehicleStatsQuery,
  useLazyListAllVehicleQuery,
  useLazyGetVehicleListQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useDeleteVehicleMutation,
} = vehiclesApi;
